import type { ProductDefinition } from './types.js';

/**
 * Customily's own English wording, as the importer and the form generator
 * used to copy it onto a field word for word: the song picker's "Spotify
 * Player", "Search for a track" and its one-line description, and the map's.
 * Shoppers read those on a Serbian shop (the live Muzički Ram did), so a field
 * that still carries one of these exact defaults is shown in Serbian. Anything
 * the merchant wrote themselves is left alone.
 */
const SERBIAN_TEXTS: ReadonlyArray<readonly [english: string, serbian: string]> = [
  ['Search for a track', 'Pretraži pesmu'],
  [
    'Let shoppers search for a song and display its details on a music player — like title, artist, album cover, or a scannable code.',
    'Pretraži i izaberi pesmu — njen naziv, izvođač, omot albuma ili kod za skeniranje pojaviće se na proizvodu.',
  ],
  ['Search Location', 'Pretraži lokaciju'],
  [
    'Map lets shoppers search for a specific location by typing an address or place name.',
    'Pronađi lokaciju — upiši adresu ili naziv mesta.',
  ],
];

/** Default field names, per option type: a text field called "Spotify Player" is the merchant's own. */
const SERBIAN_LABELS: Readonly<Record<string, ReadonlyArray<readonly [english: string, serbian: string]>>> = {
  spotify_track: [['Spotify Player', 'Spotify pesma']],
};

/** Case, spacing and the kind of dash vary between an import and a hand copy. */
const comparable = (text: string): string =>
  text.trim().replace(/\s+/g, ' ').replace(/[‐-―−]/g, '-').toLowerCase();

const lookup = (pairs: ReadonlyArray<readonly [string, string]>, text: string): string | undefined => {
  const key = comparable(text);
  return pairs.find(([english]) => comparable(english) === key)?.[1];
};

/** The Serbian for one of Customily's English defaults; any other text as it is. */
export function serbianDefault(text: string): string {
  return lookup(SERBIAN_TEXTS, text) ?? text;
}

/** Rewrites the defaults in a product's questions, in place; returns the product. */
export function localizeDefaults(product: ProductDefinition): ProductDefinition {
  for (const option of product.options ?? []) {
    const label = typeof option.label === 'string' ? lookup(SERBIAN_LABELS[option.type] ?? [], option.label) : undefined;
    if (label) option.label = label;
    if (typeof option.helpText === 'string') option.helpText = serbianDefault(option.helpText);
    const placeholder = option.config?.placeholder;
    if (typeof placeholder === 'string') option.config.placeholder = serbianDefault(placeholder);
  }
  return product;
}
